import { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { updateCacheById } from 'graphql/apollo/cache';
import { Dashboard, DashboardMainHeaderForm } from 'dashboard/components/dashboard';
import { Pagination } from 'dashboard/components/table';
import PowerDistributionUnitsTable from 'powerDistributionUnits/components/table/PowerDistributionUnitsTable';
import { useLoading } from 'ui/contexts/overlay/Loading';
import useToastContext from 'ui/hooks/useToast';
import { POWER_DISTRIBUTION_UNIT_LIST_QUERY } from 'powerDistributionUnits/graphql/powerDistributionUnitListQuery';
import { powerDistributionUnitTypename } from 'powerDistributionUnits/graphql/powerDistributionUnitQuery';
import {
  POWER_DISTRIBUTION_UNIT_DELETE_MUTATION,
  PowerDistributionUnitDeleteMutationVariables,
} from 'powerDistributionUnits/graphql/powerDistributionUnitDeleteMutaiton';
import { PowerDistributionUnit } from 'powerDistributionUnits/models/powerDistributionUnit';
import { ToastProps } from 'ui/contexts/overlay/Toast';
import { useConfirm } from 'ui/contexts/overlay/Confirm';
import { INITIAL_QUERY_STATE_CONFIG } from 'graphql/apollo/config';
import { useHistory } from 'react-router';
import AddButton from 'dashboard/components/dashboard/AddButton';

type State = {
  powerDistributionUnits: PowerDistributionUnit[];
  hasNextPage: boolean;
  hasPreviousPage: boolean;
};

const LIST_ERROR_TOAST: ToastProps = {
  title: 'Algo deu errado!',
  variant: 'danger',
  text: 'Não foi possível carregar a lista de Concessionárias',
};

const UPDATE_ERROR_TOAST: ToastProps = {
  text: 'Houve um erro ao tentar atualizar Concessionária',
  title: 'Algo deu errado!',
  variant: 'danger',
};

const UPDATE_SUCCESS_TOAST: ToastProps = {
  text: 'Concessionária atualizada com sucesso',
  title: 'Sucesso',
  variant: 'primary',
};

const DASHBOARD_POWER_DISTRIBUTION_UNITS_CREATE_ROUTE =
  '/dashboard/business-entities/power-distribution-units/create';

export default function PowerDistributionUnitsPage() {
  const { push } = useHistory();
  const { addToast } = useToastContext();
  const { LoadingOverlay, showLoading, closeLoading } = useLoading();
  const { ConfirmOverlay, closeConfirm, showConfirm } = useConfirm();
  const [currentPowerDistributionUnitId, setcurrentcurrentPowerDistributionUnitId] =
    useState<number>(0);
  const [state, setState] = useState<State>({
    powerDistributionUnits: [],
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const [
    powerDistributionUnitDeleteMutation,
    { loading: powerDistributionUnitDeleteLoading },
  ] = useMutation<PowerDistributionUnitDeleteMutationVariables>(
    POWER_DISTRIBUTION_UNIT_DELETE_MUTATION,
    {
      onError() {
        addToast(UPDATE_ERROR_TOAST);
      },
      onCompleted() {
        addToast(UPDATE_SUCCESS_TOAST);
      },
    }
  );

  const [powerDistributionUnitsList, { data, loading, error, refetch }] =
    useLazyQuery(POWER_DISTRIBUTION_UNIT_LIST_QUERY, INITIAL_QUERY_STATE_CONFIG);

  useEffect(() => {
    powerDistributionUnitsList();
  }, [powerDistributionUnitsList]);

  useEffect(() => {
    if (error) {
      addToast(LIST_ERROR_TOAST);
    }
  }, [addToast, error]);

  const isLoading = loading || powerDistributionUnitDeleteLoading;

  useEffect(() => {
    if (isLoading) {
      showLoading();
      return;
    }

    if (data) {
      setState({
        powerDistributionUnits: data.powerDistributionUnits.entries,
        hasNextPage: !!data.powerDistributionUnits.afterCursor,
        hasPreviousPage: !!data.powerDistributionUnits.beforeCursor,
      });
    }

    closeLoading();
  }, [closeLoading, data, isLoading, showLoading]);

  const handleClickNext = () => {
    refetch &&
      refetch({
        after: data.powerDistributionUnits.afterCursor,
        before: null,
      });
  };

  const handleClickBefore = () => {
    refetch &&
      refetch({
        after: null,
        before: data.powerDistributionUnits.beforeCursor,
      });
  };

  const onClickEditButton = (id: number) =>
    push(`/dashboard/business-entities/power-distribution-units/${id}/edit`);

  const onClickRemoveButton = (id: number) => {
    setcurrentcurrentPowerDistributionUnitId(id);
    showConfirm();
  };

  const onConfirmDelete = () => {
    closeConfirm();

    powerDistributionUnitDeleteMutation({
      variables: { id: currentPowerDistributionUnitId },
      update(cache) {
        updateCacheById(
          cache,
          currentPowerDistributionUnitId,
          powerDistributionUnitTypename
        );
      },
    });
  };

  const onCancelDelete = () => {
    closeConfirm();
  };

  const onClickAddButton = () =>
    push(DASHBOARD_POWER_DISTRIBUTION_UNITS_CREATE_ROUTE);

  return (
    <Dashboard
      dashboardMainHeaderTitle={
        <DashboardMainHeaderForm title="Concessionárias de Energia">
          <AddButton onClick={onClickAddButton} label="Concessionária" />
        </DashboardMainHeaderForm>
      }
    >
      <div className="rounded-lg">
        <PowerDistributionUnitsTable
          powerDistributionUnits={state.powerDistributionUnits}
          onClickEditButton={onClickEditButton}
          onClickRemoveButton={onClickRemoveButton}
        />
        <Pagination
          onNextClick={handleClickNext}
          onPreviousClick={handleClickBefore}
          disableNext={!state.hasNextPage}
          disableBefore={!state.hasPreviousPage}
        />
        <ConfirmOverlay
          title="Desativação da Concessionária!"
          text="Tem certeza que deseja desativar essa Concessionária? Todos os dados serão permanentemente removidos do nosso sistema. Essa ação não poderá ser revertida."
          variant="danger"
          onConfirm={onConfirmDelete}
          onCancel={onCancelDelete}
        />
      </div>
      <LoadingOverlay />
    </Dashboard>
  );
}
